import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

export type VenueMapPin = {
  id: number;
  name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  latitude: number;
  longitude: number;
};

type Props = {
  venues: VenueMapPin[];
  basePath: string;
  className?: string;
  onReady?: (api: VenuesMapApi) => void;
};

export type VenuesMapApi = {
  focusVenue: (venueId: number) => void;
};

export function VenuesMap({ venues, basePath, className, onReady }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<number, L.Marker>>({});

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { scrollWheelZoom: false }).setView([39.5, -98.35], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    onReady?.({
      focusVenue: (venueId: number) => {
        const m = markersRef.current[venueId];
        const target = mapRef.current;
        if (!m || !target) return;
        const ll = m.getLatLng();
        target.setView(ll, Math.max(target.getZoom(), 14), { animate: true });
        m.openPopup();
      },
    });

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    Object.values(markersRef.current).forEach((m) => m.remove());
    markersRef.current = {};

    if (venues.length === 0) return;

    const prefix = basePath.endsWith("/") ? basePath : `${basePath}/`;

    venues.forEach((venue) => {
      const marker = L.marker([venue.latitude, venue.longitude]).addTo(map);
      const fullAddress = [venue.address, venue.city, venue.state].filter(Boolean).join(", ");
      const safeName = escapeHtml(venue.name);
      const safeAddress = escapeHtml(fullAddress);
      const href = `${prefix}venues/${venue.id}`;
      marker.bindPopup(
        `<div style="min-width:160px"><strong>${safeName}</strong>${
          safeAddress ? `<br/><span style="color:#666">${safeAddress}</span>` : ""
        }<br/><a href="${href}" style="color:#b45309;text-decoration:underline">View venue →</a></div>`,
      );
      markersRef.current[venue.id] = marker;
    });

    const bounds = L.latLngBounds(venues.map((v) => [v.latitude, v.longitude] as [number, number]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [venues, basePath]);

  return <div ref={containerRef} className={className} />;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
